import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Typography, Input, Button, Table, Modal, Form, Cascader, Popconfirm, Select, DatePicker } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import '../styles/AdminScholarManagement.css';
import '../styles/SharedLayout.css';
import AdminSidebar from '../components/AdminSidebar';
import { getRequest, postRequest } from '../utilities/apicalls';
import ToastMessage from '../components/ToastMessage';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

// Define custom styles
const customStyles = `
  .scholar-modal .action-buttons-container {
    display: flex !important;
    justify-content: flex-end !important;
    width: 100% !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
    gap: 8px !important;
    overflow: visible !important;
    position: relative !important;
    right: 0 !important;
  }
  
  .scholar-modal .ant-form-item:last-child {
    margin-bottom: 0 !important;
    margin-right: 0 !important;
    padding-right: 0 !important;
    width: 100% !important;
  }
  
  .scholar-modal .ant-form {
    width: 100% !important;
    padding-right: 0 !important;
    margin-right: 0 !important;
  }
  
  .scholar-modal .ant-modal-body {
    padding: 24px !important;
    position: relative !important;
  }
  
  .scholar-modal .ant-form-item-control-input,
  .scholar-modal .ant-form-item-control-input-content {
    width: 100% !important;
    padding-right: 0 !important;
    margin-right: 0 !important;
  }
  
  .scholar-modal .float-action-buttons {
    position: absolute !important;
    bottom: 24px !important;
    right: 24px !important;
    z-index: 10 !important;
    display: flex !important;
    gap: 8px !important;
  }
`;

/**
 * AdminScholarManagement Component
 * 
 * Displays and manages scholars with filtering capabilities
 * @returns {JSX.Element} The AdminScholarManagement component
 */
const AdminScholarManagement = () => {
  // State for filters and pagination
  const [searchScholar, setSearchScholar] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allScholars, setAllScholars] = useState([]);
  const [filteredScholars, setFilteredScholars] = useState([]);
  const [selectedCascader, setSelectedCascader] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [form] = Form.useForm();
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedScholar, setSelectedScholar] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm] = Form.useForm();

  // Array of courses for filter dropdown
  const courses = useMemo(() => [
    { value: 'Information Technology', label: 'Information Technology', mobileLabel: 'IT' },
    { value: 'Computer Science', label: 'Computer Science', mobileLabel: 'CS' },
    { value: 'Information Systems', label: 'Information Systems', mobileLabel: 'IS' },
    { value: 'Computer Engineering', label: 'Computer Engineering', mobileLabel: 'CE' },
    { value: 'Software Engineering', label: 'Software Engineering', mobileLabel: 'SE' },
    { value: 'Cybersecurity', label: 'Cybersecurity', mobileLabel: 'Cybersec' },
    { value: 'Data Science', label: 'Data Science', mobileLabel: 'DS' },
    { value: 'Varsity', label: 'Varsity', mobileLabel: 'Varsity' },
    { value: 'Others', label: 'Others', mobileLabel: 'Others' }
  ], []); // Empty dependency array since courses are static

  // Function to create cascader options based on screen width
  const createCascaderOptions = useCallback((isMobile = window.innerWidth <= 425) => [
    {
      value: 'College',
      label: 'College',
      children: courses.map(course => ({
        value: course.value,
        label: isMobile ? course.mobileLabel : course.label
      }))
    },
    {
      value: 'High School',
      label: 'High School',
      children: [
        {
          value: 'ICT',
          label: 'ICT'
        },
        {
          value: 'STEM',
          label: 'STEM'
        }
        ,
        {
          value: 'JHS',
          label: 'JHS'
        },
        {
          value: 'VARSITY',
          label: 'VARSITY'
        }
      ]
    }
  ], [courses]);

  // Add state for cascader options
  const [cascaderOptions, setCascaderOptions] = useState(createCascaderOptions());

  // Add useEffect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      // Update cascader options when window is resized
      const newOptions = createCascaderOptions();
      // Force re-render of cascader by updating state
      setCascaderOptions(newOptions);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [createCascaderOptions]);

  // Function to fetch scholars from API
  const fetchScholars = async () => {
    try {
      setLoading(true);
      
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return [];
      }
      
      // Build query parameters
      let params = {
        token: token,
        requester: id,
      };
      
      // Add date range parameters if selected
      if (dateRange && dateRange.length === 2) {
        // Format dates using native JavaScript Date methods
        const formatDate = (date) => {
          if (!date) return '';
          
          const d = new Date(date);
          const year = d.getFullYear();
          // Month is 0-indexed in JS, so add 1 and pad with 0 if needed
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          
          return `${year}-${month}-${day}`;
        };
        
        const startDate = formatDate(dateRange[0]);
        const endDate = formatDate(dateRange[1]);
        
        // Specify that date filtering is based on contract signing date
        params.date_field = 'contract_signing_date';
        params.date_from = startDate;
        params.date_to = endDate;
      }
      
      // Use the getRequest function from apicalls.jsx
      const response = await getRequest('scholars/get', params);
      
      if (response.success && response.data) {
        // Ensure response.data is an array before filtering
        let dataToProcess = response.data;
        
        // Convert to array if it's not already
        if (!Array.isArray(dataToProcess)) {
          if (dataToProcess.data && Array.isArray(dataToProcess.data)) {
            // If response has a data property that's an array
            dataToProcess = dataToProcess.data;
          } else {
            // If it's a single object or has another format
            dataToProcess = [dataToProcess];
          }
        }
        
        // Now safely filter the array
        const scholars = dataToProcess
          .filter(scholar => scholar) // Filter out null/undefined items
          .map((scholar, index) => {
            try {
              // Safely access properties with fallbacks
              const scholarId = scholar.id || scholar.scholar_id || `temp_${index}_${Date.now()}`;
              const firstName = scholar.first_name || '';
              const middleName = scholar.middle_name || '';
              const lastName = scholar.last_name || '';
              const suffix = scholar.suffix || '';
              const email = scholar.email || '';
              const gender = scholar.gender || '';
              const scholarType = (scholar.scholar_type || '').toLowerCase();
              const type = scholarType.includes('high') ? 'High School' : 'College';
              const course = scholar.course || '';
              const status = scholar.status || 'Active'; // Default to Active if not specified
              
              // Add additional fields from the API response
              const contractSigningDate = scholar.contract_signing_date || null;
              const birthday = scholar.birthday || null;
              const contactNumber = scholar.contact_number || '';
              const address = scholar.address || '';
              const bank = scholar.bank || '';
              const bankAccountName = scholar.bank_account_name || '';
              const bankAccountNumber = scholar.bank_account_number || '';
              
              return {
                key: scholarId,
                scholar_id: scholarId,
                name: `${firstName} ${middleName ? middleName + ' ' : ''}${lastName}${suffix ? ' ' + suffix : ''}`,
                firstName,
                middleName,
                lastName,
                suffix,
                email,
                gender,
                type,
                course: type === 'College' ? course : null,
                strand: type === 'High School' ? course : null,
                status,
                createdAt: scholar.date_created || new Date().toISOString(),
                // Add mapped additional fields
                contractSigningDate,
                birthday,
                contactNumber,
                address,
                bank,
                bankAccountName,
                bankAccountNumber
              };
            } catch (err) {
              return null;
            }
          })
          .filter(Boolean); // Remove any nulls from errors in processing
        
        setAllScholars(scholars);
        setFilteredScholars(scholars);
        
        if (scholars && scholars.length > 0) {
          // message was removed
        } else {
          // If we filtered out all scholars due to data issues
          if (dateRange && dateRange.length === 2) {
            ToastMessage.info('No valid scholars found for the selected date range');
          } else {
            ToastMessage.info('No valid scholars found');
          }
        }
      } else {
        throw new Error('Failed to retrieve scholars data');
      }
    } catch (error) {
      if (error.response) {
        ToastMessage.error(`Failed to load scholars: ${error.response.data?.message || error.response.data?.error || 'Server error'}`);
      } else if (error.request) {
        ToastMessage.error('No response from server. Please check your network connection.');
      } else {
        ToastMessage.error(`Error: ${error.message}`);
      }
      
      // Use empty array if fetch fails
      setAllScholars([]);
      setFilteredScholars([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch scholars when component mounts OR dateRange changes
    fetchScholars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]); // Only keep this one useEffect

  // Reset filters when scholars are fetched from API
  useEffect(() => {
    // Only run when allScholars changes and selectedCascader is empty
    if ((!selectedCascader || selectedCascader.length === 0) && 
        (!searchScholar || searchScholar.trim() === '')) {
      // If no filters active, set filteredScholars to all scholars
      setFilteredScholars(allScholars || []);
    }
  }, [allScholars, searchScholar, selectedCascader]);

  // Handle scholar search input change
  const handleSearchChange = (e) => {
    if (!e || !e.target) {
      return;
    }
    
    const value = e.target.value;
    setSearchScholar(value);
    
    // Apply direct filtering similar to cascader change
    let result = Array.isArray(allScholars) ? [...allScholars] : [];
    
    // Filter by search term if it exists
    if (value && value.trim() !== '') {
      const searchTerm = value.toLowerCase();
        result = result.filter(scholar => 
          scholar.name.toLowerCase().includes(searchTerm) ||
          scholar.email.toLowerCase().includes(searchTerm)
        );
      }
      
    // Also apply type/course filter if active
    if (selectedCascader && Array.isArray(selectedCascader) && selectedCascader.length > 0) {
        const scholarType = selectedCascader[0];
        result = result.filter(scholar => scholar.type === scholarType);
        
        if (selectedCascader.length > 1) {
        const courseOrStrand = selectedCascader[1];
          if (scholarType === 'College') {
          result = result.filter(scholar => scholar.course === courseOrStrand);
          } else if (scholarType === 'High School') {
          result = result.filter(scholar => scholar.strand === courseOrStrand);
        }
      }
    }
    
    // Update results and reset page
    setFilteredScholars(result);
    setCurrentPage(1);
    
    // Show message if no results but we have scholars and search term
    if (result.length === 0 && Array.isArray(allScholars) && allScholars.length > 0 && value.trim() !== '') {
      ToastMessage.info(`No scholars found matching search term "${value}"`);
    }
  };

  // Handle cascader change
  const handleCascaderChange = (value) => {
    // If value is null/undefined/empty, reset the filter
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setSelectedCascader([]);
      
      // Immediately update filtered scholars to show all scholars when filter is cleared
      setFilteredScholars(allScholars || []);
      return;
    } else {
      // Ensure value is always an array
      setSelectedCascader(Array.isArray(value) ? value : []);
    }
    
    // Apply filters immediately for real-time filtering
    // Don't use setTimeout - apply filters synchronously
    const scholarType = Array.isArray(value) && value.length > 0 ? value[0] : null;
    const courseOrStrand = Array.isArray(value) && value.length > 1 ? value[1] : null;
    
    // Apply filter immediately without waiting for state update
    if (scholarType) {
      let result = Array.isArray(allScholars) ? [...allScholars] : [];
      
      // Filter by scholar name if search term exists
      if (searchScholar && searchScholar.trim() !== '') {
        const searchTerm = searchScholar.toLowerCase();
        result = result.filter(scholar => 
          scholar.name.toLowerCase().includes(searchTerm) ||
          scholar.email.toLowerCase().includes(searchTerm)
        );
      }
      
      // Filter by type
      result = result.filter(scholar => scholar.type === scholarType);
      
      // Filter by course/strand if selected
      if (courseOrStrand) {
        if (scholarType === 'College') {
          result = result.filter(scholar => scholar.course === courseOrStrand);
        } else if (scholarType === 'High School') {
          result = result.filter(scholar => scholar.strand === courseOrStrand);
        }
      }
      
      // Update filtered results immediately
      setFilteredScholars(result);
      setCurrentPage(1);
      
      // Show message if no results
      if (result.length === 0 && Array.isArray(allScholars) && allScholars.length > 0) {
        const filterDescription = courseOrStrand 
          ? `${scholarType} - ${courseOrStrand}` 
          : scholarType;
        ToastMessage.info(`No scholars found for ${filterDescription}`);
      }
    }
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    // Update state with new date range (can be null when cleared)
    // Ensure we set to null if dates is undefined or empty array
    setDateRange(dates && Array.isArray(dates) && dates.length === 2 ? dates : null);
    // The fetch will be triggered by the useEffect watching dateRange
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Scholar Name',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      titleAlign: 'left',
      onHeaderCell: () => ({
        style: { textAlign: 'left' },
      }),
      render: (text) => text, // Directly display the name without any number
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      align: 'left',
      titleAlign: 'left',
      onHeaderCell: () => ({
        style: { textAlign: 'left' }
      })
    },
    {
      title: <div style={{ textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'flex-start' }}>Scholar Type</div>,
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      width: 150,
      className: 'scholar-type-column',
      onHeaderCell: () => ({
        style: { 
          textAlign: 'left', 
          justifyContent: 'flex-start',
          paddingLeft: '16px'
        },
        className: 'scholar-type-header'
      }),
      render: (text) => <span style={{ textAlign: 'left', display: 'block' }}>{text}</span>
    },
    {
      title: 'Strand',
      dataIndex: 'strand',
      key: 'strand',
      align: 'left',
      render: (text) => text || '-'
    },
    {
      title: 'Course',
      dataIndex: 'course',
      key: 'course',
      align: 'left',
      titleAlign: 'left',
      onHeaderCell: () => ({
        style: { textAlign: 'left' }
      }),
      render: (text, record) => {
        if (record.type === 'High School') {
          return '-';
        }
        return text;
      }
    },
    {
      title: <div style={{ textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center' }}>Status</div>,
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 120,
      onHeaderCell: () => ({
        style: { 
          textAlign: 'center', 
          justifyContent: 'center'
        }
      }),
      render: (text, record) => {
        // Normalize the status text
        const normalizedStatus = String(text || 'Active').trim();
        
        // Determine colors based on status
        const statusColors = {
          Active: {
            color: '#52c41a', // Green for Active
            textColor: '#fff'
          },
          Inactive: {
            color: '#fa8c16', // Orange for Inactive
            textColor: '#fff'
          }
        };
        
        // Get color settings for current status
        const currentColor = statusColors[normalizedStatus] || { color: '#d9d9d9', textColor: 'rgba(0, 0, 0, 0.65)' };
        
        return (
          <div style={{ 
            backgroundColor: currentColor.color,
            color: currentColor.textColor,
            borderRadius: '4px',
            padding: '4px 8px',
            display: 'inline-block',
            textAlign: 'center',
            minWidth: '80px'
          }}>
            {normalizedStatus}
          </div>
        );
      }
    }
  ];

  const handleRowClick = (record) => {
    if (!record) {
      return;
    }
    
    setSelectedScholar(record);
    setIsViewModalVisible(true);
    setIsEditing(false);
    
    // Reset the edit form with the scholar's data
    if (editForm) {
      setTimeout(() => {
        // Use existing firstName, middleName, lastName fields if they exist
        // otherwise fallback to splitting the name field
        let firstName = record.firstName;
        let middleName = record.middleName;
        let lastName = record.lastName;
        let suffix = record.suffix;
        
        // If the individual name fields don't exist, try to extract from full name
        if (!firstName && record.name) {
          const nameParts = record.name ? record.name.split(' ') : [];
          if (nameParts.length === 1) {
            firstName = nameParts[0] || '';
            middleName = '';
            lastName = '';
            suffix = '';
          } else if (nameParts.length === 2) {
            firstName = nameParts[0] || '';
            middleName = '';
            lastName = nameParts[1] || '';
            suffix = '';
          } else {
            // Check for common suffixes in the last part
            const lastPart = nameParts[nameParts.length - 1] || '';
            const commonSuffixes = ['Jr.', 'Sr.', 'I', 'II', 'III', 'IV', 'V'];
            
            if (commonSuffixes.includes(lastPart)) {
              firstName = nameParts[0] || '';
              lastName = nameParts.length > 2 ? (nameParts[nameParts.length - 2] || '') : '';
              suffix = lastPart;
              middleName = nameParts.length > 3 ? nameParts.slice(1, -2).join(' ') : '';
            } else {
              firstName = nameParts[0] || '';
              lastName = nameParts.length > 1 ? (nameParts[nameParts.length - 1] || '') : '';
              suffix = '';
              middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
            }
          }
        }
        
        // Process date values from string to dayjs objects
        // Parse all dates regardless of whether they might be auto-generated
        let contractSigningDate = null;
        let contractSourceDate = record.contractSigningDate || record.contract_signing_date;
        if (contractSourceDate) {
          try {
            contractSigningDate = dayjs(contractSourceDate);
          } catch (error) {
            // If we can't parse it, assume it's auto-generated
          }
        }
        
        let birthday = null;
        let birthdaySourceDate = record.birthday;
        if (birthdaySourceDate) {
          try {
            birthday = dayjs(birthdaySourceDate);
          } catch (error) {
            // If we can't parse it, assume it's auto-generated
          }
        }
        
        // Set form values with safety checks
        editForm.setFieldsValue({
          firstName: firstName || '',
          middleName: middleName || '',
          lastName: lastName || '',
          suffix: suffix || '',
          email: record.email || '',
          gender: record.gender || '',
          typeCourse: record.type === 'College' 
            ? [record.type || 'College', record.course || '']
            : [record.type || 'High School', record.strand || ''],
          status: record.status || 'Active', // Set the status field
          // Always set date values if they exist - don't filter out potentially auto-generated dates
          contractSigningDate: contractSigningDate,
          birthday: birthday,
          contactNumber: record.contactNumber || record.contact_number || '',
          address: record.address || '',
          bank: record.bank || '',
          bankAccountName: record.bankAccountName || record.bank_account_name || '',
          bankAccountNumber: record.bankAccountNumber || record.bank_account_number || ''
        });
      }, 100);
    }
  };

  const handleSidebarCollapse = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  const showModal = () => {
    setIsModalVisible(true);
    // Delay form reset to ensure the Modal is rendered first
    setTimeout(() => {
      form.resetFields();
    }, 0);
  };

  const handleCancel = () => {
    // First hide the modal
    setIsModalVisible(false);
    // Use slight delay to ensure modal animation completes
    setTimeout(() => {
      form.resetFields();
    }, 100);
  };

  const handleFormSubmit = async (values) => {
    try {
      // Before proceeding, validate the form fields again
      await form.validateFields();
      
      setLoading(true);
      
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      // Create a FormData object for the API request
      const formData = new FormData();
      
      // Required fields - matching exactly what backend expects
      formData.append('first_name', values.firstName);
      formData.append('middle_name', values.middleName || '');
      formData.append('last_name', values.lastName);
      formData.append('suffix', values.suffix || '');
      formData.append('gender', values.gender);
      formData.append('email', values.email);
      
      // Ensure typeCourse is correctly formatted before accessing
      const scholarType = Array.isArray(values.typeCourse) && values.typeCourse.length > 0 
        ? values.typeCourse[0] : 'College';
      const courseName = Array.isArray(values.typeCourse) && values.typeCourse.length > 1 
        ? values.typeCourse[1] : '';
        
      formData.append('scholar_type', scholarType === 'High School' ? 'Highschool' : 'College');
      formData.append('course', courseName); // Always use course field for both college and high school
      
      // Authentication fields
      formData.append('token', token);
      
      // According to the backend code, requester is mapped to added_by
      formData.append('requester', id); // This will be used as added_by in backend
      
      // Include updated_by as a separate field (also required)
      formData.append('updated_by', id);
      
      // DO NOT include status - backend sets it directly to 'Active'
      
      // Check if date values are provided by user
      // If not, use auto-generated values (today's date) for backend
      const isContractDateProvided = values.contractSigningDate && values.contractSigningDate.isValid();
      const isBirthdayProvided = values.birthday && values.birthday.isValid();
      
      // Use today's date as fallback, but with a special prefix in the database
      // to identify it as auto-generated
      const today = dayjs().format('YYYY-MM-DD');
      const contractDate = isContractDateProvided 
        ? values.contractSigningDate.format('YYYY-MM-DD')
        : ''; // Auto-generated date
        
      const birthdayDate = isBirthdayProvided
        ? values.birthday.format('YYYY-MM-DD')
        : ''; // Auto-generated date
      
      formData.append('contract_signing_date', contractDate);
      formData.append('birthday', birthdayDate);
      
      // Add remaining fields
      formData.append('contact_number', values.contactNumber || '');
      formData.append('address', values.address || '');
      formData.append('bank', values.bank || '');
      formData.append('bank_account_name', values.bankAccountName || '');
      formData.append('bank_account_number', values.bankAccountNumber || '');
      
      // Use postRequest from apicalls.jsx with FormData flags
      const response = await postRequest('scholars/create', formData, true, true);
      
      // console.log('API Response:', response);

      if (response.status === 200) {
        // After successful creation, refresh scholar list
        await fetchScholars();
        
        ToastMessage.success('Scholar added successfully');
        form.resetFields();
        setIsModalVisible(false);
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to add scholar';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // console.error('Error adding scholar:', error);
      
      // If it's a validation error, don't show toast as the form will display errors
      if (error.errorFields) {
        setLoading(false);
        return;
      }
      
      // Handle API errors using our consistent pattern
      const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to add scholar';
      ToastMessage.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCancel = () => {
    // First hide the modal
    setIsViewModalVisible(false);
    // Use a slight delay before resetting other states to ensure
    // modal animations complete and React has time to update the DOM
    setTimeout(() => {
      setSelectedScholar(null);
      setIsEditing(false);
      // Reset form if it exists
      if (editForm) {
        editForm.resetFields();
      }
    }, 100);
  };

  // Add a new function with event parameter that stops propagation
  const handleEditClickWithPrevent = (e) => {
    // Prevent the click event from propagating to the form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsEditing(true);
  };

  const handleUpdateScholar = async (values) => {
    try {
      // Check if this is a legitimate update with changes
      // Don't proceed if we just switched to edit mode
      if (!isEditing) {
        // console.log('Update prevented - not in edit mode');
        ToastMessage.info('Update prevented - not in edit mode');
        return;
      }
      
      setLoading(true);
      
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      // Ensure we're using the correct numeric scholar_id, not the key
      const scholarId = selectedScholar.scholar_id || selectedScholar.key;
      
      // Create a FormData object for the API request
      const formData = new FormData();
      
      // Required fields - matching exactly what backend expects
      formData.append('scholar_id', scholarId);
      formData.append('first_name', values.firstName);
      formData.append('middle_name', values.middleName || '');
      formData.append('last_name', values.lastName);
      formData.append('suffix', values.suffix || '');
      formData.append('gender', values.gender);
      formData.append('email', values.email);
      
      // Ensure typeCourse is correctly formatted before accessing
      const scholarType = Array.isArray(values.typeCourse) && values.typeCourse.length > 0 
        ? values.typeCourse[0] : 'College';
      const courseName = Array.isArray(values.typeCourse) && values.typeCourse.length > 1 
        ? values.typeCourse[1] : '';
      
      formData.append('scholar_type', scholarType === 'High School' ? 'Highschool' : 'College');
      formData.append('course', courseName); // Always use course field for both college and high school
      
      // Authentication fields
      formData.append('token', token);
      formData.append('requester', id); // This maps to updated_by in backend
      
      // Include status for update - backend needs this for scholar/update endpoint
      formData.append('status', values.status || 'Active');
      
      // Check if date values are provided by user
      // If not, use auto-generated values (today's date) for backend
      const isContractDateProvided = values.contractSigningDate && values.contractSigningDate.isValid();
      const isBirthdayProvided = values.birthday && values.birthday.isValid();
      
      // Use today's date as fallback
      const today = dayjs().format('YYYY-MM-DD');
      const contractDate = isContractDateProvided 
        ? values.contractSigningDate.format('YYYY-MM-DD')
        : ''; // Default to today if not provided
      
      const birthdayDate = isBirthdayProvided
        ? values.birthday.format('YYYY-MM-DD')
        : ''; // Default to today if not provided
      
      formData.append('contract_signing_date', contractDate);
      formData.append('birthday', birthdayDate);
      
      // Add remaining fields
      formData.append('contact_number', values.contactNumber || '');
      formData.append('address', values.address || '');
      formData.append('bank', values.bank || '');
      formData.append('bank_account_name', values.bankAccountName || '');
      formData.append('bank_account_number', values.bankAccountNumber || '');
      
      // Use postRequest from apicalls.jsx with FormData flags
      const response = await postRequest('scholars/update', formData, true, true);
      
      // console.log('API Response:', response);

      if (response.status === 200) {
        // After successful update, update local state with new data
        const updatedScholar = {
          ...selectedScholar,
          name: `${values.firstName} ${values.middleName ? values.middleName + ' ' : ''}${values.lastName}${values.suffix ? ' ' + values.suffix : ''}`,
          firstName: values.firstName,
          middleName: values.middleName || '',
          lastName: values.lastName,
          suffix: values.suffix || '',
          email: values.email,
          gender: values.gender,
          type: values.typeCourse[0],
          course: values.typeCourse[0] === 'College' ? values.typeCourse[1] : null,
          strand: values.typeCourse[0] === 'High School' ? values.typeCourse[1] : null,
          status: values.status || 'Active',
          // Keep track of all provided dates
          contractSigningDate: contractDate,
          contactNumber: values.contactNumber || '',
          address: values.address || '',
          birthday: birthdayDate,
          bank: values.bank || '',
          bankAccountName: values.bankAccountName || '',
          bankAccountNumber: values.bankAccountNumber || ''
        };
        
        // Update local lists for immediate UI update
        setAllScholars(prevScholars => 
          prevScholars.map(scholar => 
            scholar.key === selectedScholar.key ? updatedScholar : scholar
          )
        );
        
        setFilteredScholars(prevScholars => 
          prevScholars.map(scholar => 
            scholar.key === selectedScholar.key ? updatedScholar : scholar
          )
        );
        
        // Also refresh data from API
        await fetchScholars();
        
        ToastMessage.success('Scholar updated successfully');
        setIsEditing(false);
        setIsViewModalVisible(false);
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to update scholar';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // console.error('Error updating scholar:', error);
      
      // Handle API errors using our consistent pattern
      const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to update scholar';
      ToastMessage.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScholar = async () => {
    try {
      setLoading(true);
      
      // Check if a scholar is selected
      if (!selectedScholar) {
        ToastMessage.error('No scholar selected for deletion');
        setLoading(false);
        return;
      }
      
      // Ensure we're using the numeric scholar_id
      const scholarId = selectedScholar.scholar_id || selectedScholar.key;
      
      if (!scholarId) {
        ToastMessage.error('No valid scholar ID found for deletion');
        setLoading(false);
        return;
      }
      
      // Get authentication data from localStorage
      const token = localStorage.getItem('token');
      const id = localStorage.getItem('id');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !id) {
        ToastMessage.error('Authentication data not found. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Check if token is expired
      if (tokenExpiry && new Date(tokenExpiry) < new Date()) {
        // Token expired, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('tokenExpiry');
        ToastMessage.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
      
      // Create a FormData object for the API request
      const formData = new FormData();
      formData.append('scholar_id', scholarId);
      formData.append('token', token);
      formData.append('requester', id);
      
      // Use postRequest from apicalls.jsx with FormData flags
      const response = await postRequest('scholars/delete', formData, true, true);
      
      // console.log('API Response:', response);

      if (response.status === 200) {
        // Immediately remove the scholar from the lists
        setAllScholars(prevScholars => prevScholars.filter(scholar => scholar.key !== selectedScholar.key));
        setFilteredScholars(prevScholars => prevScholars.filter(scholar => scholar.key !== selectedScholar.key));
        
        // Also refresh scholar list from API
        await fetchScholars();
        
        ToastMessage.success('Scholar deleted successfully');
        setIsViewModalVisible(false);
        setSelectedScholar(null);
      } else {
        // Handle non-200 response
        const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to delete scholar';
        ToastMessage.error(errorMessage);
      }
    } catch (error) {
      // console.error('Error deleting scholar:', error);
      
      // Handle API errors using our consistent pattern
      const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to delete scholar';
      ToastMessage.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="admin-layout">
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <AdminSidebar
        activePage="scholars"
        onCollapse={handleSidebarCollapse}
      />
      <Content className={`admin-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="admin-scholar-container">
          <Title level={4}>Scholar Management</Title>
          
          {/* Filters Section */}
          <div className="filters-section">
            <RangePicker
              className="date-range-filter"
              placeholder={['Date From', 'Date To']}
              value={dateRange || null}
              onChange={handleDateRangeChange}
              allowClear
            />
            
            <Cascader
              className="cascader-filter"
              options={cascaderOptions}
              placeholder="Scholar Type & Course"
              expandTrigger="hover"
              changeOnSelect
              value={selectedCascader || []}
              onChange={handleCascaderChange}
              allowClear
              notFoundContent="No options found"
              displayRender={(labels) => {
                if (!labels || labels.length === 0) return 'All Scholar Types';
                // Convert full course name to mobile label if on mobile
                if (window.innerWidth <= 425 && labels.length > 1) {
                  const course = courses.find(c => c.value === labels[1]);
                  return `${labels[0]} - ${course ? course.mobileLabel : labels[1]}`;
                }
                return labels.join(' - ');
              }}
            />
            
            <Input
              className="scholar-search"
              placeholder="Search by name or email"
              value={searchScholar || ''}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              allowClear
            />
          </div>
          
          {/* Add New Scholar Button */}
          <div className="add-button-container">
            <Button 
              type="primary" 
              className="add-button"
              onClick={showModal}
              icon={<PlusOutlined />}
            >
              Add New Scholar
            </Button>
          </div>
          
          {/* Scholars Table */}
          <Table
            className="scholars-table"
            columns={columns}
            dataSource={filteredScholars || []}
            rowClassName="scholar-row"
            bordered={false}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            pagination={{
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              pageSize: pageSize,
              total: filteredScholars.length,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, filteredScholars.length.toString()],
              responsive: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomCenter'],
              onShowSizeChange: (current, size) => {
                setCurrentPage(1); // Reset to first page when changing page size
                setPageSize(size);
              },
              itemRender: (page, type, element) => {
                if (type === 'page') {
                  return <button type="button" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>{page}</button>;
                }
                return element;
              }
            }}
            loading={loading}
            locale={{
              emptyText: (
                <div style={{ padding: '20px 0' }}>
                  <p>No scholar records found</p>
                  {(selectedCascader && Array.isArray(selectedCascader) && selectedCascader.length > 0) || 
                   (searchScholar && searchScholar.trim() !== '') || 
                   (dateRange && Array.isArray(dateRange) && dateRange.length === 2) ? (
                    <small>Try removing some filters or adjusting your search criteria</small>
                  ) : (
                    <small>Add a new scholar to get started</small>
                  )}
                </div>
              ),
              Pagination: {
                items_per_page: '/ page',
                jump_to: 'Go to',
                page: 'Page',
                prev_page: 'Previous Page',
                next_page: 'Next Page',
                prev_5: 'Previous 5 Pages',
                next_5: 'Next 5 Pages'
              }
            }}
          />
        </div>
        
        {/* Copyright Footer */}
        <div className="copyright-footer">
          ©2025 MYT Softdev Solutions Inc.
        </div>
      </Content>
      
      {/* Add Scholar Modal */}
      <Modal
        title="Add New Scholar"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="scholar-modal"
        destroyOnClose={true}
        maskClosable={false}
        style={{ 
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      >
        {isModalVisible && (
          <div style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto', paddingRight: '5px' }}>
            <Form 
              form={form} 
              layout="vertical" 
              onFinish={handleFormSubmit}
              validateTrigger={['onChange', 'onBlur']}
            >
              <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: 'Please enter the first name' }]}> 
                <Input placeholder="Enter first name" /> 
              </Form.Item>
              
              <Form.Item name="middleName" label="Middle Name"> 
                <Input placeholder="Enter middle name" /> 
              </Form.Item>

              <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Please enter the last name' }]}> 
                <Input placeholder="Enter last name" /> 
              </Form.Item>

              <Form.Item name="suffix" label="Suffix"> 
                <Input placeholder="Enter suffix (e.g. Jr., Sr., III)" /> 
              </Form.Item>

              <Form.Item name="email" label="Email Address" rules={[
                { required: true, message: 'Please enter email address' }, 
                { type: 'email', message: 'Please enter a valid email' }
              ]}> 
                <Input placeholder="Enter email address" /> 
              </Form.Item>

              <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Please select gender' }]}> 
                <Select placeholder="Select gender"> 
                  <Select.Option value="Male">Male</Select.Option> 
                  <Select.Option value="Female">Female</Select.Option> 
                </Select> 
              </Form.Item>

              <Form.Item 
                name="typeCourse" 
                label="Scholar Type & Course" 
                rules={[{ 
                  required: true, 
                  message: 'Please select scholar type and course',
                  validator: (_, value) => {
                    if (Array.isArray(value) && value.length === 2) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please select both scholar type and course'));
                  }
                }]}
              > 
                <Cascader 
                  options={cascaderOptions} 
                  placeholder="Select type and course" 
                  expandTrigger="hover"
                  changeOnSelect={false} 
                /> 
              </Form.Item>

              <Form.Item name="status" label="Status" initialValue="Active">
                <Select>
                  <Select.Option value="Active" style={{ color: '#52c41a' }}>Active</Select.Option>
                  <Select.Option value="Inactive" style={{ color: '#fa8c16' }}>Inactive</Select.Option>
                </Select>
              </Form.Item>

              {/* Additional Fields */}
              <Form.Item 
                name="contractSigningDate" 
                label="Date of Contract Signing"
              > 
                <DatePicker placeholder="Select date" /> 
              </Form.Item>

              <Form.Item 
                name="birthday" 
                label="Birthday"
              > 
                <DatePicker placeholder="Select birthday" /> 
              </Form.Item>

              <Form.Item name="contactNumber" label="Contact Number"> 
                <Input placeholder="Enter contact number" /> 
              </Form.Item>

              <Form.Item name="address" label="Address"> 
                <Input placeholder="Enter address" /> 
              </Form.Item>

              <Form.Item name="bank" label="Bank"> 
                <Input placeholder="Enter bank name" /> 
              </Form.Item>

              <Form.Item name="bankAccountName" label="Bank Account Name"> 
                <Input placeholder="Enter bank account name" /> 
              </Form.Item>

              <Form.Item name="bankAccountNumber" label="Bank Account Number"> 
                <Input placeholder="Enter bank account number" /> 
              </Form.Item>

              <Form.Item>
                <div className="button-container">
                  <Button type="default" onClick={handleCancel}> Cancel </Button>
                  <Button type="primary" htmlType="submit" loading={loading}> Add Scholar </Button>
                </div>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
      
      {/* View/Edit Scholar Modal */}
      <Modal
        title={isEditing ? "Edit Scholar" : "Scholar Details"}
        open={isViewModalVisible}
        onCancel={handleViewCancel}
        footer={null}
        className="scholar-modal"
        destroyOnClose={true}
        maskClosable={true}
        style={{ 
          top: '50%',
          transform: 'translateY(-50%)'
        }}
        styles={{
          body: {
            padding: '24px 24px 24px 24px',
            overflow: 'hidden',
            position: 'relative'
          }
        }}
      >
        {isViewModalVisible && selectedScholar && (
          <div style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto', paddingRight: '0', paddingBottom: '50px' }}>
            <Form
              form={editForm}
              layout="vertical"
              onFinish={handleUpdateScholar}
              initialValues={{
                firstName: '',
                middleName: '',
                lastName: '',
                suffix: '',
                email: '',
                gender: '',
                typeCourse: ['College', ''],
                status: selectedScholar.status || 'Active'
              }}
              style={{ width: '100%' }}
              onSubmit={(e) => {
                // Only submit the form when explicitly triggered by the Update button
                if (!isEditing) {
                  e.preventDefault();
                }
              }}
            >
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter the first name' }]}
              >
                <Input placeholder="Enter first name" disabled={!isEditing} />
              </Form.Item>
              
              <Form.Item
                name="middleName"
                label="Middle Name"
              >
                <Input placeholder="Enter middle name" disabled={!isEditing} />
              </Form.Item>
              
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter the last name' }]}
              >
                <Input placeholder="Enter last name" disabled={!isEditing} />
              </Form.Item>
              
              <Form.Item
                name="suffix"
                label="Suffix"
              >
                <Input placeholder="Enter suffix (e.g. Jr., Sr., III)" disabled={!isEditing} />
              </Form.Item>
              
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter email address' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="Enter email address" disabled={!isEditing} />
              </Form.Item>
              
              <Form.Item
                name="gender"
                label="Gender"
                rules={[{ required: true, message: 'Please select gender' }]}
              >
                <Select placeholder="Select gender" disabled={!isEditing}>
                  <Select.Option value="Male">Male</Select.Option>
                  <Select.Option value="Female">Female</Select.Option>
                </Select>
              </Form.Item>
              
              <Form.Item
                name="typeCourse"
                label="Scholar Type & Course"
                rules={[{ required: true, message: 'Please select scholar type and course' }]}
                className="ant-form-item-cascader"
              >
                <Cascader
                  options={cascaderOptions}
                  placeholder="Select type and course"
                  expandTrigger="hover"
                  disabled={!isEditing}
                />
              </Form.Item>
              
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status" disabled={!isEditing}>
                  <Select.Option value="Active" style={{ color: '#52c41a' }}>Active</Select.Option>
                  <Select.Option value="Inactive" style={{ color: '#fa8c16' }}>Inactive</Select.Option>
                </Select>
              </Form.Item>
              
              {/* Additional Fields matching Add Scholar Modal */}
              <Form.Item 
                name="contractSigningDate" 
                label="Date of Contract Signing"
              > 
                <DatePicker placeholder="Select date" disabled={!isEditing} />
              </Form.Item>

              <Form.Item 
                name="birthday" 
                label="Birthday"
              > 
                <DatePicker placeholder="Select birthday" disabled={!isEditing} /> 
              </Form.Item>

              <Form.Item name="contactNumber" label="Contact Number">
                <Input placeholder="Enter contact number" disabled={!isEditing} />
              </Form.Item>

              <Form.Item name="address" label="Address">
                <Input placeholder="Enter address" disabled={!isEditing} />
              </Form.Item>

              <Form.Item name="bank" label="Bank">
                <Input placeholder="Enter bank name" disabled={!isEditing} />
              </Form.Item>

              <Form.Item name="bankAccountName" label="Bank Account Name">
                <Input placeholder="Enter bank account name" disabled={!isEditing} />
              </Form.Item>

              <Form.Item name="bankAccountNumber" label="Bank Account Number">
                <Input placeholder="Enter bank account number" disabled={!isEditing} />
              </Form.Item>
              
              {/* Buttons are removed from the Form.Item structure */}
              {isEditing && (
                <Form.Item style={{ marginTop: '24px', marginBottom: '0', marginRight: '0', paddingRight: '0', width: '100%' }}>
                  <div className="action-buttons-container" style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '8px',
                    width: '100%'
                  }}>
                    <Button 
                      type="default" 
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update
                    </Button>
                  </div>
                </Form.Item>
              )}
            </Form>
            
            {/* Floating action buttons outside the form when not editing */}
            {!isEditing && (
              <div className="float-action-buttons" style={{
                position: 'absolute',
                bottom: '24px',
                right: '24px',
                display: 'flex',
                gap: '8px',
                zIndex: 10
              }}>
                <Popconfirm
                  title="Are you sure you want to delete this scholar?"
                  onConfirm={handleDeleteScholar}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    type="primary" 
                    danger
                    icon={<DeleteOutlined />}
                  >
                    Delete
                  </Button>
                </Popconfirm>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={handleEditClickWithPrevent}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default AdminScholarManagement;